import BaseDrawer from "@/shared/components/Drawer";
import { DraftIndicator } from "@/shared/ui/DraftIndicator";
import { Separator } from "@/shared/ui/Separator";
import { useSupplierDraft } from "../hooks/useSupplierDraft";
import { useSuppliersMutations } from "../hooks/useSupplierMutations";
import SupplierForm from "./SupplierForm";
import type { SupplierFormData } from "../schemas/supplier.schema";
import type { Supplier } from "../types/suppliers.types";

type SupplierDrawerProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  data?: Supplier
  width?: string
  zIndex?: number
};

const SupplierDrawer = ({
  open, onOpenChange, data, width, zIndex,
}: SupplierDrawerProps) => {
  const { createSupplierMutation, updateSupplierMutation } = useSuppliersMutations();

  // ----- DRAFT CONTEXT ----- //
  const { getChangedFields, removeDraft } = useSupplierDraft();
  const supplierId = data?.id || null;
  const changedFields = getChangedFields(supplierId);
  const isEditing = Boolean(data);
  const isLoading = createSupplierMutation.isPending || updateSupplierMutation.isPending;

  const handleFormChange = (_hasChanges: boolean) => {
    // This is handled by the draft context
  };

  const handleCancel = () => {
    removeDraft(supplierId);
    onOpenChange(false);
  };

  const handleSubmitAction = async (values: SupplierFormData) => {
    try {
      if (isEditing && data) {
        await updateSupplierMutation.mutateAsync({
          id: data.id,
          supplier: values,
        });
      }
      else {
        await createSupplierMutation.mutateAsync({ supplier: values });
      }
      onOpenChange(false);
      // Remove draft after successful submission
      removeDraft(supplierId);
    }
    catch {
      // Error is handled by the mutation meta configuration
    }
  };

  return (
    <BaseDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={isEditing ? "Modifier l'entreprise" : "Ajouter une entreprise"}
      description="Gérez les informations de l'entreprise."
      width={width}
      zIndex={zIndex}
      headerIndicator={
        <DraftIndicator hasChanges={changedFields.length > 0} />
      }
    >
      <Separator className="my-4" />
      <SupplierForm
        supplier={data}
        onSubmitAction={handleSubmitAction}
        onFormChange={handleFormChange}
        onCancel={handleCancel}
        isLoading={isLoading}
        isEditMode={isEditing}
      />
    </BaseDrawer>
  );
};

export default SupplierDrawer;
